/**
 * 모달 열림 상태에 따른 폼 리셋 커스텀 훅
 *
 * 모달이 열릴 때 폼을 기본값으로 리셋하는 로직을 추상화합니다.
 * useForm의 reset 메서드를 안전하게 호출하며, 의존성 관리를 간소화합니다.
 *
 * @example
 * // 기본 사용
 * useFormReset(isOpen, form, { trackCount: 1 });
 *
 * @example
 * // 동적 기본값
 * useFormReset(isOpen && selectedTrack !== null, form, {
 *   title: selectedTrack?.title || '',
 *   lyrics: selectedTrack?.lyrics || '',
 * });
 */

import { useEffect, useRef, useCallback } from 'react';
import type { UseFormReset, FieldValues } from 'react-hook-form';

/**
 * 모달 열림 상태에 따라 폼을 리셋하는 훅
 *
 * @param isOpen - 모달이 열려있는지 여부
 * @param reset - useForm의 reset 함수
 * @param defaultValues - 리셋 시 적용할 기본값
 */
export function useFormReset<T extends FieldValues>(
  isOpen: boolean,
  reset: UseFormReset<T>,
  defaultValues: T
): void {
  // defaultValues를 ref로 관리하여 불필요한 리렌더링 방지
  const defaultValuesRef = useRef(defaultValues);

  // defaultValues가 변경될 때만 ref 업데이트
  useEffect(() => {
    defaultValuesRef.current = defaultValues;
  }, [defaultValues]);

  // reset 함수를 메모이제이션
  const stableReset = useCallback(() => {
    reset(defaultValuesRef.current);
  }, [reset]);

  // 모달이 열릴 때 폼 리셋
  useEffect(() => {
    if (isOpen) {
      stableReset();
    }
  }, [isOpen, stableReset]);
}

/**
 * 조건부 모달 열림 상태에 따라 폼을 리셋하는 훅
 * 추가 조건(예: selectedItem !== null)이 필요한 경우 사용
 *
 * @param condition - 리셋 조건 (isOpen && 추가 조건)
 * @param reset - useForm의 reset 함수
 * @param getDefaultValues - 기본값을 반환하는 함수
 */
export function useConditionalFormReset<T extends FieldValues>(
  condition: boolean,
  reset: UseFormReset<T>,
  getDefaultValues: () => T
): void {
  // 함수를 ref로 관리
  const getDefaultValuesRef = useRef(getDefaultValues);

  useEffect(() => {
    getDefaultValuesRef.current = getDefaultValues;
  }, [getDefaultValues]);

  const stableReset = useCallback(() => {
    reset(getDefaultValuesRef.current());
  }, [reset]);

  useEffect(() => {
    if (condition) {
      stableReset();
    }
  }, [condition, stableReset]);
}
